import { Component } from "react";
import { connect } from "react-redux";

import InMemoryTableWithSelectionModel from "../../core/grid/InMemoryTableWithSelectionModel.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";
import { type ISelectOption } from "@/app-utils/form.ts";
import { type GenomeAssembly, type InputNcbiParamsInputData } from "@/graphql.ts";
import { type INCBIDownloadOptions } from "@/redux/modules/ncbi-admin.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { CELL_TYPE } from "@/settings/settings.js";

interface INCBIAgentReviewScreenState {
  isDownloading?: boolean;
  errorMessage?: string;
}

interface INCBIAgentReviewScreenOwnProps extends IPlaybookWizardStepProvidedProps {
  onDownLoad: (options: InputNcbiParamsInputData[]) => Promise<GenomeAssembly[]>;
}

interface INCBIAgentReviewScreenAppStateProps {
  downloadParamLookup: Record<string, INCBIDownloadOptions>; // map by accession
  selectedGenomes: GenomeAssembly[];
}

interface INCBIAgentReviewScreenDispatchProps {}

export interface INCBIAgentReviewScreenProps
  extends INCBIAgentReviewScreenAppStateProps,
    INCBIAgentReviewScreenDispatchProps,
    INCBIAgentReviewScreenOwnProps,
    IPlaybookWizardStepInjectedProps {}

function mapStateToProps(state: IAppState): INCBIAgentReviewScreenAppStateProps {
  return {
    downloadParamLookup: state.ncbiAgent.downloadParamLookup,
    selectedGenomes: state.ncbiAgent.selectedGenomes,
  };
}

function mapDispatchToProps(_dispatch): INCBIAgentReviewScreenDispatchProps {
  return {};
}

class INCBIAgentReviewScreenImpl extends Component<INCBIAgentReviewScreenProps, INCBIAgentReviewScreenState> {
  state = {
    isDownloading: false,
    errorMessage: null,
  };

  getDownloadParams = (useValues = false): InputNcbiParamsInputData[] => {
    return this.props.selectedGenomes.map((g: GenomeAssembly): InputNcbiParamsInputData => {
      const params: INCBIDownloadOptions = this.props.downloadParamLookup[g.assemblyAccession];
      return {
        ...params,
        taxonomy: useValues ? params.taxonomy.value : params.taxonomy.label,
        kingdom: useValues ? params.kingdom.value : params.kingdom.label,
        groups: params.groups.map((o: ISelectOption) => (useValues ? o.value : o.label)),
      } as InputNcbiParamsInputData;
    });
  };

  render() {
    const rowData = this.getDownloadParams().map((ncbniParam: InputNcbiParamsInputData) => {
      return {
        id: ncbniParam.assembly,
        ...ncbniParam,
      };
    });

    return (
      <PlaybookWizardStep
        {...this.props}
        wizardFooterData={{
          error: this.state.errorMessage,
          nextButtonProps: {
            label: "Download",
            onPress: this.handleSubmitData,
            isLoading: this.state.isDownloading,
          },
        }}
      >
        <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
          <InMemoryTableWithSelectionModel
            columns={ncbiAgentColumnDefs}
            rowGridData={rowData}
            useSelectionModel={false}
            gridProps={{
              domLayout: "autoHeight",
            }}
          />
        </div>
      </PlaybookWizardStep>
    );
  }

  handleSubmitData = (_values) => {
    this.setState({ errorMessage: null, isDownloading: true });

    return this.props.onDownLoad(this.getDownloadParams(true)).then(
      (_fulfilled) => {
        this.setState({ isDownloading: false });
        this.props.proceedWithNextStep();
      },
      (error: Error) => {
        this.setState({ errorMessage: error.message, isDownloading: false });
      },
    );
  };
}

const ncbiAgentColumnDefs: any[] = [
  {
    name: "Assembly Accession",
    field: "assembly",
    cellType: CELL_TYPE.STRING,
  },
  {
    name: "Genome Identifier",
    field: "identifier",
    cellType: CELL_TYPE.STRING,
  },
  {
    name: "Genome Name",
    field: "name",
    cellType: CELL_TYPE.STRING,
  },
  {
    name: "Strain",
    field: "strain",
    cellType: CELL_TYPE.STRING,
  },
  {
    name: "Species",
    field: "species",
    cellType: CELL_TYPE.STRING,
  },
  {
    name: "Taxonomy",
    field: "taxonomy",
    cellType: CELL_TYPE.STRING,
  },
  {
    name: "Transcript Handling",
    field: "kingdom",
    cellType: CELL_TYPE.STRING,
  },
  {
    name: "User Group(s)",
    field: "groups",
    cellType: CELL_TYPE.STRING,
  },
];

const INCBIAgentReviewScreen = connect<
  INCBIAgentReviewScreenAppStateProps,
  INCBIAgentReviewScreenDispatchProps,
  INCBIAgentReviewScreenOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(INCBIAgentReviewScreenImpl as any);

export default INCBIAgentReviewScreen;
export { INCBIAgentReviewScreen };
